import { getSupabase } from "../auth/supabaseClient";
import { getCharacterById, getDefaultCharacter, migrateLegacyId, isLegacyId, resolveCharacterImagePath, type CharacterDef } from "./characters";

export interface OwnedCosmetic {
  cosmeticId: string;
  acquiredAt: string;
  source: string;
}

// ── Local cache ───────────────────────────────────────────────────────────

const INVENTORY_CACHE_KEY = "bitbeast.inventory.v1";
const EQUIPPED_CACHE_KEY = "bitbeast.equipped.v1";

function migrateLegacyIds(ids: string[]): string[] {
  return ids.map(migrateLegacyId);
}

function loadCachedInventory(): string[] {
  try {
    const raw = localStorage.getItem(INVENTORY_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as string[];
      const migrated = migrateLegacyIds(parsed);
      if (migrated.some((id, i) => id !== parsed[i])) {
        saveCachedInventory(migrated);
      }
      return migrated;
    }
  } catch { /* ignore */ }
  return [];
}

function saveCachedInventory(ids: string[]): void {
  try {
    localStorage.setItem(INVENTORY_CACHE_KEY, JSON.stringify(ids));
  } catch { /* ignore */ }
}

function loadCachedEquipped(): string {
  try {
    const raw = localStorage.getItem(EQUIPPED_CACHE_KEY) ?? "";
    if (raw && isLegacyId(raw)) {
      const migrated = migrateLegacyId(raw);
      saveCachedEquipped(migrated);
      return migrated;
    }
    return raw;
  } catch { /* ignore */ }
  return "";
}

function saveCachedEquipped(id: string): void {
  try {
    localStorage.setItem(EQUIPPED_CACHE_KEY, id);
  } catch { /* ignore */ }
}

let cachedOwnedIds: string[] = loadCachedInventory();
let cachedEquipped: string = loadCachedEquipped();

/** Reset all inventory caches (localStorage + in-memory). Call on sign-out. */
export function resetInventoryCache(): void {
  cachedOwnedIds = [];
  cachedEquipped = "";
  try {
    localStorage.removeItem(INVENTORY_CACHE_KEY);
    localStorage.removeItem(EQUIPPED_CACHE_KEY);
  } catch { /* ignore */ }
}

// ── Public API ────────────────────────────────────────────────────────────

export function getCachedOwnedIds(): string[] {
  return [...cachedOwnedIds];
}

export function getCachedEquipped(): string {
  return cachedEquipped;
}

export function getCachedEquippedCharacter(): CharacterDef {
  const resolved = migrateLegacyId(cachedEquipped);
  const catalogDef = getCharacterById(resolved);
  if (catalogDef) return catalogDef;
  // NFT avatar not in catalog — build synthetic def from skin ID
  if (resolved && /\d/.test(resolved)) {
    const imagePath = resolveCharacterImagePath(resolved);
    const numMatch = resolved.match(/(\d+)/);
    const num = numMatch ? Number(numMatch[1]) : 0;
    return {
      ...getDefaultCharacter(),
      id: resolved,
      name: `BitBeast #${num}`,
      imagePath,
      imageSmallPath: imagePath,
    };
  }
  return getDefaultCharacter();
}

export function primeCachedEquipped(equippedId: string | null | undefined): void {
  const resolved = migrateLegacyId((equippedId ?? "").trim());
  if (!resolved) {
    return;
  }
  if (!getCharacterById(resolved)) {
    return;
  }
  cachedEquipped = resolved;
  saveCachedEquipped(cachedEquipped);
}

export async function fetchInventory(userId: string): Promise<string[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("user_cosmetics")
    .select("cosmetic_id")
    .eq("user_id", userId);

  if (error || data == null) {
    console.warn("[inventory] fetchInventory failed:", error?.message ?? "no data");
    return cachedOwnedIds;
  }

  const dbIds = migrateLegacyIds(data.map((row) => row.cosmetic_id));
  // Merge: keep any locally-added IDs that the DB doesn't have yet
  // (protects against read-replica lag after a purchase RPC commit)
  const dbSet = new Set(dbIds);
  const merged = [...dbIds];
  for (const localId of cachedOwnedIds) {
    if (!dbSet.has(localId)) {
      merged.push(localId);
    }
  }
  cachedOwnedIds = merged;
  saveCachedInventory(cachedOwnedIds);
  return cachedOwnedIds;
}

export async function fetchEquipped(userId: string): Promise<string> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("profiles")
    .select("equipped_character")
    .eq("id", userId)
    .single();

  if (error || data == null) {
    console.warn("[inventory] fetchEquipped failed:", error?.message ?? "no data");
    return cachedEquipped;
  }

  const dbEquipped = migrateLegacyId(data.equipped_character ?? "");
  // Only update cache if DB has a real value; don't overwrite local with empty
  if (dbEquipped) {
    cachedEquipped = dbEquipped;
    saveCachedEquipped(cachedEquipped);
  }
  return cachedEquipped;
}

export async function equipCharacter(userId: string, characterId: string): Promise<boolean> {
  const resolved = migrateLegacyId(characterId);
  // Must own the character
  if (!cachedOwnedIds.includes(resolved)) {
    console.warn("[inventory] equipCharacter: not owned", resolved);
    return false;
  }

  // Must be a valid character
  if (!getCharacterById(resolved)) {
    console.warn("[inventory] equipCharacter: unknown character", resolved);
    return false;
  }

  // Optimistic update — reflect change in UI immediately
  cachedEquipped = resolved;
  saveCachedEquipped(cachedEquipped);

  const supabase = getSupabase();
  const { error } = await supabase
    .from("profiles")
    .update({ equipped_character: resolved, updated_at: new Date().toISOString() })
    .eq("id", userId);

  if (error) {
    console.warn("[inventory] equipCharacter DB failed, saving locally:", error.message);
  }

  return true;
}

export async function purchaseCharacter(
  userId: string,
  characterId: string
): Promise<{ ok: boolean; error?: string; gemsRemaining?: number }> {
  const resolved = migrateLegacyId(characterId);
  const charDef = getCharacterById(resolved);
  if (!charDef) return { ok: false, error: "Unknown character" };
  if (charDef.priceGems <= 0) return { ok: false, error: "Character is not purchasable" };
  if (cachedOwnedIds.includes(resolved)) return { ok: false, error: "Already owned" };

  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("purchase_character", {
    p_user_id: userId,
    p_character_id: resolved,
    p_price: charDef.priceGems,
  });

  if (error || !data?.ok) {
    return { ok: false, error: error?.message ?? data?.error ?? "Purchase failed" };
  }

  // Update local caches
  cachedOwnedIds.push(resolved);
  saveCachedInventory(cachedOwnedIds);

  return { ok: true, gemsRemaining: data.gems };
}

/** For guest/offline: mark a character as owned locally. */
export function addOwnedLocal(characterId: string): void {
  const resolved = migrateLegacyId(characterId);
  if (!cachedOwnedIds.includes(resolved)) {
    cachedOwnedIds.push(resolved);
    saveCachedInventory(cachedOwnedIds);
  }
}

/** For guest/offline: equip a character locally. */
export function equipCharacterLocal(characterId: string): boolean {
  const resolved = migrateLegacyId(characterId);
  if (!cachedOwnedIds.includes(resolved)) return false;
  if (!getCharacterById(resolved)) return false;
  cachedEquipped = resolved;
  saveCachedEquipped(cachedEquipped);
  return true;
}
